import React from 'react';
import type { AnnouncementDto, TimetableDto, TeacherSubjectDto } from '../../api/axios';
import type { SchoolCode } from '../../types';
import { SHARED_SCHOOL_MOTTO } from '../../schoolBranding';
import { formatClassLevelLabel } from '../../utils/classLevelLabel';

interface DashboardViewProps {
  teacherName: string;
  assignments: any[];
  teacherSubjects: TeacherSubjectDto[];
  announcements: AnnouncementDto[];
  timetables: TimetableDto[];
  loadingAnnouncements: boolean;
  loadingTimetables: boolean;
  onNavigate: (section: string) => void;
  school?: SchoolCode | string;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  teacherName,
  assignments,
  teacherSubjects,
  announcements,
  timetables,
  loadingAnnouncements,
  loadingTimetables,
  onNavigate,
  school = 'KBA',
}) => {
  const getDayName = (dayIndex: number): string => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[dayIndex];
  };

  const getTodayAndTomorrow = () => {
    const today = new Date();
    const todayIndex = today.getDay();
    const tomorrowIndex = (todayIndex + 1) % 7;
    
    return {
      today: getDayName(todayIndex),
      tomorrow: getDayName(tomorrowIndex),
    };
  };

  const getTimetableForDay = (day: string) => {
    return timetables
      .filter((tt) => tt.day === day.toLowerCase())
      .sort((a, b) => a.period - b.period);
  };

  const formatTime = (timeString: string | null): string => {
    if (!timeString) return '';
    const timePart = timeString.includes('T') 
      ? timeString.split('T')[1]?.split(':').slice(0, 2).join(':') 
      : timeString.split(':').slice(0, 2).join(':');
    return timePart || '';
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getNextClass = () => {
    const { today, tomorrow } = getTodayAndTomorrow();
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    const todayTimetable = getTimetableForDay(today);
    for (const entry of todayTimetable) {
      const classTime = formatTime(entry.start_time || null);
      if (classTime && classTime > currentTime) {
        return { entry, isToday: true };
      }
    }

    const tomorrowTimetable = getTimetableForDay(tomorrow);
    if (tomorrowTimetable.length > 0) {
      return { entry: tomorrowTimetable[0], isToday: false };
    }

    return null;
  };

  const { today } = getTodayAndTomorrow();
  const todayClasses = getTimetableForDay(today).sort((a, b) => {
    const timeA = formatTime(a.start_time || null);
    const timeB = formatTime(b.start_time || null);
    return timeA.localeCompare(timeB);
  });
  const nextClass = getNextClass();

  // Modern styles
  const containerStyle: React.CSSProperties = {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: 32,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 28,
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.5px',
    marginBottom: 6,
    lineHeight: 1.2,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: 15,
    color: '#475569',
    lineHeight: 1.5,
  };

  const statsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 20,
    marginBottom: 32,
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const statCardStyle = (_gradient: string): React.CSSProperties => ({
    background: '#fff',
    padding: 24,
    borderRadius: 16,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -1px rgba(0, 0, 0, 0.04)',
    border: '1px solid #f1f5f9',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  });

  const statTopBorderStyle = (gradient: string): React.CSSProperties => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: gradient,
  });

  const twoColumnStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 24,
    marginBottom: 32,
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -1px rgba(0, 0, 0, 0.04)',
    border: '1px solid #f1f5f9',
    overflow: 'hidden',
  };

  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 700,
    color: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  };

  const cardBodyStyle: React.CSSProperties = {
    padding: 24,
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    background: '#fff',
    color: '#475569',
    border: '2px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '48px 24px',
    background: '#f8fafc',
    borderRadius: 12,
    border: '2px dashed #e2e8f0',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Welcome back, {teacherName ? teacherName.split(' ')[0] : 'Teacher'}! 👋</h1>
        <p style={subtitleStyle}>Here's an overview of your day and recent activity</p>
        <p
          style={{
            margin: '12px 0 0',
            maxWidth: '34rem',
            fontSize: 13,
            lineHeight: 1.5,
            fontStyle: 'italic',
            color: '#64748b',
            fontFamily: "Georgia, 'Times New Roman', serif",
          }}
        >
          {SHARED_SCHOOL_MOTTO}
        </p>
      </div>

      {/* Stats Grid */}
      <div style={statsGridStyle}>
        {[
          { icon: '📝', label: 'Active Assignments', value: assignments.length, gradient: '#2563eb', onClick: () => onNavigate('assignments') },
          { icon: '👥', label: 'Classes Assigned', value: teacherSubjects.length, gradient: '#059669' },
          { icon: '📅', label: "Today's Classes", value: todayClasses.length, gradient: '#4b5563', onClick: () => onNavigate('timetable') },
          { icon: '📢', label: 'Announcements', value: announcements.length, gradient: '#d97706', onClick: () => onNavigate('announcements') },
        ].map((stat, i) => (
          <div
            key={i}
            style={statCardStyle(stat.gradient)}
            onClick={stat.onClick}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -1px rgba(0, 0, 0, 0.04)';
            }}
          >
            <div style={statTopBorderStyle(stat.gradient)}></div>
            <div style={{ 
              width: 48, 
              height: 48, 
              borderRadius: 12, 
              background: 'rgba(37, 99, 235, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              marginBottom: 16,
            }}>
              {stat.icon}
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#0f172a', marginBottom: 4, lineHeight: 1 }}>
              {stat.value}
            </div>
            <div style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Two Column Layout */}
      <div style={twoColumnStyle}>
        {/* Next Class */}
        {nextClass ? (
          <div style={{
            background: '#2563eb',
            color: '#fff',
            borderRadius: 16,
            padding: 28,
            boxShadow: '0 8px 24px rgba(30, 64, 175, 0.25)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '100%',
              height: '200%',
              background: 'none',
              pointerEvents: 'none',
            }}></div>
            <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.9, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>📍</span> Next Class
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.5px' }}>
              {nextClass.entry.subject?.name || 'No Subject'}
            </div>
            <div style={{ fontSize: 15, opacity: 0.9, marginBottom: 20 }}>
              {formatClassLevelLabel(school, nextClass.entry.form)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {nextClass.entry.start_time && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 500 }}>
                  <span>⏰</span>
                  <span>
                    {formatTime(nextClass.entry.start_time || null)}
                    {nextClass.entry.end_time && ` - ${formatTime(nextClass.entry.end_time || null)}`}
                  </span>
                </div>
              )}
              {nextClass.entry.room && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 500 }}>
                  <span>🏫</span>
                  <span>Room {nextClass.entry.room}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 500 }}>
                <span>📆</span>
                <span>{nextClass.isToday ? 'Today' : 'Tomorrow'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', padding: 32 }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>🎉</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#475569' }}>
                No upcoming classes
              </div>
              <div style={{ fontSize: 14, color: '#94a3b8', marginTop: 4 }}>
                Enjoy your free time!
              </div>
            </div>
          </div>
        )}

        {/* Today's Schedule Summary */}
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
              📅 Today's Schedule
            </div>
            {loadingTimetables ? (
              <div style={{ padding: 20, color: '#94a3b8' }}>Loading...</div>
            ) : (
              <>
                <div style={{ fontSize: 48, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                  {todayClasses.length}
                </div>
                <div style={{ fontSize: 16, color: '#475569', marginBottom: 24 }}>
                  {todayClasses.length === 1 ? 'Class' : 'Classes'} Today
                </div>
                <button
                  style={buttonStyle}
                  onClick={() => onNavigate('timetable')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.color = '#1e40af';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#475569';
                  }}
                >
                  View Full Schedule →
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Today's Classes */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={cardHeaderStyle}>
          <div style={cardTitleStyle}>
            <span>📅</span> Today's Classes
          </div>
          <button
            style={{ ...buttonStyle, padding: '8px 16px', fontSize: 12 }}
            onClick={() => onNavigate('timetable')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.borderColor = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            View All →
          </button>
        </div>
        <div style={cardBodyStyle}>
          {loadingTimetables ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading timetable...</div>
          ) : todayClasses.length === 0 ? (
            <div style={emptyStateStyle}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.6 }}>🎉</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                No classes scheduled for today
              </div>
              <div style={{ fontSize: 14, color: '#94a3b8' }}>
                Enjoy your free day! Check the full timetable for other days.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 400, overflowY: 'auto' }}>
              {todayClasses.map((entry) => {
                const isNextClass = nextClass?.entry.id === entry.id && nextClass.isToday;
                return (
                  <div
                    key={entry.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '100px 1fr auto',
                      gap: 20,
                      alignItems: 'center',
                      padding: 18,
                      background: isNextClass ? 'rgba(245, 158, 11, 0.06)' : '#f8fafc',
                      borderRadius: 12,
                      border: isNextClass ? '2px solid #f59e0b' : '1px solid #f1f5f9',
                      transition: 'all 0.2s',
                      position: 'relative',
                      minWidth: 0,
                    }}
                  >
                    {isNextClass && (
                      <div style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        background: '#d97706',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: 20,
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                        Next
                      </div>
                    )}
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1e40af', fontFamily: "'SF Mono', 'Monaco', monospace" }}>
                      {formatTime(entry.start_time || null)}
                      {entry.end_time && (
                        <>
                          <br />
                          <span style={{ opacity: 0.6 }}>to {formatTime(entry.end_time || null)}</span>
                        </>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                        {entry.subject?.name || 'No Subject'}
                      </div>
                      <div style={{ fontSize: 13, color: '#94a3b8' }}>
                        {formatClassLevelLabel(school, entry.form)} • Period {entry.period}
                      </div>
                    </div>
                    {entry.room && (
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#475569',
                        padding: '8px 14px',
                        background: '#fff',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        whiteSpace: 'nowrap',
                        marginLeft: 'auto',
                      }}>
                        🏫 Room {entry.room}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Announcements Preview */}
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <div style={cardTitleStyle}>
            <span>📢</span> Recent Announcements
          </div>
          <button
            style={{ ...buttonStyle, padding: '8px 16px', fontSize: 12 }}
            onClick={() => onNavigate('announcements')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.borderColor = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            View All →
          </button>
        </div>
        <div style={cardBodyStyle}>
          {loadingAnnouncements ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading announcements...</div>
          ) : announcements.length === 0 ? (
            <div style={emptyStateStyle}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.6 }}>📭</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                No announcements
              </div>
              <div style={{ fontSize: 14, color: '#94a3b8' }}>
                Check back later for updates and important information.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {announcements.slice(0, 3).map((announcement) => (
                <div
                  key={announcement.id}
                  style={{
                    padding: 20,
                    background: '#f8fafc',
                    borderRadius: 12,
                    border: '1px solid #f1f5f9',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                  }}
                  onClick={() => onNavigate('announcements')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#f1f5f9';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: 'rgba(37, 99, 235, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      flexShrink: 0,
                    }}>
                      📌
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                        {announcement.title}
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, marginBottom: 8 }}>
                        🕒 {formatTimeAgo(announcement.created_at)}
                      </div>
                      <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>
                        {announcement.message}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
